import { nanoid } from 'nanoid';
import { isEqual, isFunction, isNil, map } from 'lodash/fp';
import { handleCollaboratorActions } from 'app/store/collaboration/actions.js';
import { handleServerSentMessage } from 'app/store/serverEvents/actions.js';
import { selectCurrentUserName } from 'app/store/users/selectors.js';
import { KEYCLOAK_TOKEN_REFRESHED, KEYCLOAK_TOKEN_REFRESH } from 'app/store/users/types.js';
import { COMMAND_CHAIN_COMPLETED } from 'app/store/commandChain/types';
import { doCleanupAndUpdateAppHealthStatus } from 'app/store/healthcheck/actions';
import { doReportWebsocketError, doReportWebsocketStatus } from 'app/store/websocketStatus';
import { doCommandChainCompleted, doCommandChainStarted } from 'app/store/commandChain/actions';
import { extractData } from './utils.js';
import {
    HTTP_NOT_OK,
    HTTP_OK,
    HTTP_FORBIDDEN,
    HTTP_UNAUTHORIZED,
    HTTP_SERVER_TIMEOUT,
    API_FORBIDDEN,
    API_UNAUTHORIZED
} from './constants';
import { WebSocketClient } from './WebSocketClient.js';

export function webSocketMiddleware(refreshJwtToken) {
    return ({ dispatch, getState }) => {
        const requests = {};

        function handleVersion1Message(response) {
            const { _context, data, errors, status, sessionId } = response;
            const request = _context && requests[_context.id];
            const meta = { ...request?.meta, ..._context.meta };
            const state = getState();

            if (_context.HealthCheck === 'true') {
                dispatch(doCleanupAndUpdateAppHealthStatus(data));
                return;
            }

            // if the payload of a response is nil then this response is from a followup message
            // the direct response does not have a payload

            if (!isNil(data?.payloadData)) {
                if (status >= HTTP_OK && status < HTTP_NOT_OK) {
                    const { _type: type, ...payload } = data.payloadData;
                    const params = {
                        type,
                        payload: {
                            meta,
                            ...payload,
                            performedBy: data.performedBy,
                            currentUser: selectCurrentUserName(state)
                        },
                        _context,
                        sessionId
                    };

                    // if the `_context.type` is set for COMMAND_CHAIN_COMPLETED, it is a follow up to a UI triggered command
                    // otherwise it's triggered from a follow up of server triggered events (like scheduling), we can ignore it then

                    if (type === COMMAND_CHAIN_COMPLETED && _context.type) {
                        const TYPE_COMMAND_CHAIN_COMPLETED = `${_context.type}/${COMMAND_CHAIN_COMPLETED}`;
                        dispatch({
                            type: TYPE_COMMAND_CHAIN_COMPLETED,
                            payload: params.payload,
                            _context
                        });
                        dispatch(doCommandChainCompleted(_context.type, _context.id));
                    } else {
                        dispatch(handleServerSentMessage(params));
                    }
                } else {
                    dispatch(doReportWebsocketError(status, map('message', errors)));
                }
                return;
            }

            // if we got here, this is a response message to a request we sent

            if (!request) {
                return;
            }

            const { transformer = extractData, onSucceeded, onFailed, onFinish, params } = request;

            const TYPE_SUCCEEDED = `${_context.type}/succeeded`;
            const TYPE_CANCELED = `${_context.type}/canceled`;
            const TYPE_FAILED = `${_context.type}/failed`;
            const TYPE_TIMEDOUT = `${_context.type}/timedout`;
            const TYPE_ENDED = `${_context.type}/ended`;

            if (status >= HTTP_OK && status < HTTP_NOT_OK) {
                const payload = transformer({ params, data });

                dispatch({ type: TYPE_SUCCEEDED, payload, params, meta });

                if (onSucceeded) {
                    onSucceeded({ dispatch, getState, params, payload, data, sessionId });
                }
            } else {
                const error = {
                    code: status,
                    messages: map('message', errors),
                    data
                };

                if (status === HTTP_UNAUTHORIZED) {
                    dispatch({ type: API_UNAUTHORIZED, payload: error });
                } else if (status === HTTP_FORBIDDEN) {
                    dispatch({ type: API_FORBIDDEN, payload: error, params });
                } else if (status === HTTP_SERVER_TIMEOUT) {
                    dispatch({ type: TYPE_TIMEDOUT, payload: error, params, meta });
                } else if (data?._type?.endsWith('Canceled')) {
                    dispatch({ type: TYPE_CANCELED, payload: error, params, meta });
                } else {
                    dispatch({ type: TYPE_FAILED, payload: error, params, meta });

                    if (onFailed) {
                        onFailed({ dispatch, getState, params, data, sessionId });
                    }
                }
            }

            if (isFunction(onFinish)) {
                onFinish({ dispatch, getState, meta });
            }
            dispatch({ type: TYPE_ENDED, params, meta });
            delete requests[response._context.id];
        }

        function handleVersion2Message(response) {
            const request = requests[response.meta.requestId];
            if (!request) {
                handleCollaboratorActions(dispatch, getState, response);
                return;
            }

            const meta = { ...request.meta, ...response.meta };
            const { status, responseName, payload } = response;
            const { type, onProgress, onSucceeded, onRejected, onFailed, onFinish, params } =
                request;

            const TYPE_PROGRESS = `${type}/progress`;
            const TYPE_SUCCEEDED = `${type}/succeeded`;
            const TYPE_FAILED = `${type}/failed`;
            const TYPE_REJECTED = `${type}/rejected`;
            const TYPE_ENDED = `${type}/ended`;

            switch (status) {
                case 'Progress':
                    dispatch({ type: TYPE_PROGRESS, status, payload, params, meta, responseName });
                    if (isFunction(onProgress)) {
                        onProgress({ dispatch, getState, responseName, payload, meta });
                    }
                    break;
                case 'Succeeded':
                    dispatch({ type: TYPE_SUCCEEDED, status, payload, params, meta, responseName });
                    if (isFunction(onSucceeded)) {
                        onSucceeded({ dispatch, getState, payload, params, meta, responseName });
                    }
                    if (isFunction(onFinish)) {
                        onFinish({ dispatch, getState, payload, meta });
                    }
                    dispatch({ type: TYPE_ENDED, status, payload, meta });
                    break;
                case 'Rejected':
                    dispatch({ type: TYPE_REJECTED, status, payload, meta });
                    if (isFunction(onRejected)) {
                        onRejected({ dispatch, getState, payload, meta });
                    }
                    if (isFunction(onFinish)) {
                        onFinish({ dispatch, getState, payload, meta });
                    }
                    dispatch({ type: TYPE_ENDED, status, payload, meta });
                    break;
                case 'Failed':
                    dispatch({ type: TYPE_FAILED, status, payload, params, meta });
                    if (isFunction(onFailed)) {
                        onFailed({ dispatch, getState, payload, meta });
                    }
                    if (isFunction(onFinish)) {
                        onFinish({ dispatch, getState, payload, meta });
                    }
                    dispatch({ type: TYPE_ENDED, status, payload, meta });
                    break;
                default:
            }
        }

        function handleServerMessage(response) {
            if (response.hasOwnProperty('meta')) {
                handleVersion2Message(response);
            } else {
                handleVersion1Message(response);
            }
        }

        let prevStatus = null;
        const client = WebSocketClient({
            onReceive: handleServerMessage,
            onStatusChange: (status) => {
                if (!isEqual(prevStatus, status)) {
                    dispatch(doReportWebsocketStatus(status));
                    prevStatus = status;
                }
            }
        });
        client.connect();

        return (next) => (action) => {
            const { type, api, context, params, meta } = action;

            // Currently only used by WEBSOCKET_CONNECT action.
            if (meta?.reconnect) {
                client.connect();
                return;
            }

            if (!api || api.sync) {
                return next(action);
            }

            let message;
            const requestId = nanoid();

            requests[requestId] = action;

            if (api.version === 2) {
                message = {
                    name: api.name,
                    payload: action.params,
                    meta: {
                        context,
                        requestId,
                        sessionId: null
                    }
                };
            } else {
                const { _type, endpoint } = api;
                message = {
                    _context: { id: requestId, client: context, type, meta },
                    _type,
                    ...params
                };
                if (endpoint === 'command') {
                    dispatch(doCommandChainStarted(type, requestId));
                }
            }

            dispatch({ type: `${type}/started`, payload: params, meta: { ...meta, requestId } });

            refreshJwtToken({
                onSuccess: (token) => {
                    if (token) {
                        const requestId = nanoid();
                        client.send({
                            name: 'Authenticate',
                            meta: { requestId },
                            payload: { jwt: token }
                        });

                        dispatch({ type: KEYCLOAK_TOKEN_REFRESHED });
                    }

                    client.send(message);
                },
                onError: () => {
                    dispatch({ type: KEYCLOAK_TOKEN_REFRESH.FAILED });
                }
            });
        };
    };
}
